import axios from "axios";
import { SET_MC_OPS_META, SET_MC_OPS_STATE } from "./types";
import {
  getApiBase,
  getMcToken,
  setMcToken,
  clearMcToken,
  getViewAs,
  setViewAs,
} from "./apiBase";

const getApiBasePath = () => `${getApiBase()}/mc-ops`;

const apiHeaders = () => {
  const token = getMcToken();
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/**
 * Every call carries the Bearer token; the backend derives the coordinator
 * from it. An admin may add view_as to act as a coordinator.
 */
const mcConfig = () => {
  const viewAs = getViewAs();
  const params = {};
  if (viewAs) params.view_as = viewAs;
  return {
    headers: apiHeaders(),
    ...(Object.keys(params).length ? { params } : {}),
  };
};

/** Log in with the same stage credentials; stores the returned Bearer token. */
export const mcLogin =
  ({ email, password }) =>
  async (dispatch) => {
    try {
      const { data } = await axios.post(
        `${getApiBase()}/login`,
        { email, password },
        { headers: { Accept: "application/json", "Content-Type": "application/json" } },
      );
      const token = data?.token;
      if (!token) throw new Error("No token returned");
      setMcToken(token);
      dispatch(setMcOpsMeta({ authRequired: false, authError: null }));
      return true;
    } catch (error) {
      const msg =
        error?.response?.status === 401
          ? "Invalid email or password"
          : "Login failed — please try again";
      dispatch(setMcOpsMeta({ authError: msg }));
      return false;
    }
  };

export const mcLogout = () => (dispatch) => {
  clearMcToken();
  dispatch(setMcOpsMeta({ authRequired: true }));
};

export const setMcOpsState = (payload) => ({
  type: SET_MC_OPS_STATE,
  payload,
});

export const setMcOpsMeta = (payload) => ({
  type: SET_MC_OPS_META,
  payload,
});

/**
 * Load the dashboard state. GET /api/mc-ops/state
 * On failure the board is left as it was (or empty on first load) and
 * meta.error is set; the page then shows LoadErrorScreen. It is never
 * replaced with sample data: a save on top of that would overwrite the
 * coordinator's real state.
 */
export const getMcOpsState =
  ({ showLoading = false } = {}) =>
  async (dispatch) => {
    try {
      if (showLoading) dispatch({ type: "LOADING_SHOW" });

      const { data } = await axios.get(`${getApiBasePath()}/state`, mcConfig());

      const payload = data?.data ?? data;
      if (!payload || !Array.isArray(payload.jobs)) {
        throw new Error("Unexpected /mc-ops/state payload");
      }

      dispatch(setMcOpsState(normalize(payload)));
      dispatch(
        setMcOpsMeta({
          loaded: true,
          source: "api",
          error: null,
          mc: payload.mc ?? null,
        }),
      );
      return payload;
    } catch (error) {
      if (error?.response?.status === 401) {
        clearMcToken();
        dispatch(setMcOpsMeta({ loaded: true, authRequired: true }));
        return null;
      }
      // 403: signed in, but without the mc_ops_dashboard_access permission —
      // the login is fine, so keep it and show the server's own message.
      const msg =
        (error?.response?.status === 403 && error?.response?.data?.message) || error.message;
      console.error("MC Ops: state load failed —", msg);
      dispatch(setMcOpsMeta({ loaded: true, source: null, error: msg }));
      return null;
    } finally {
      if (showLoading) dispatch({ type: "LOADING_HIDE" });
    }
  };

/**
 * Write-back is live: POST /api/mc-ops/state persists the coordinator's
 * escalations, reminders, notifications, and per-job overrides to the
 * mc_ops_states table. Zoho-owned job fields are re-hydrated on every read.
 */
export const WRITE_BACK_ENABLED = true;

/**
 * Single-flight saver.
 *
 * Each field edit persists immediately (see saveMcOpsState). The payload is
 * slimmed to only override-bearing jobs, so there is no request storm to
 * debounce — and firing immediately means a quick refresh right after an edit
 * cannot drop the save. We only ensure at most one POST is in flight at a time;
 * a burst (tabbing through fields) coalesces to the newest snapshot.
 */
/**
 * The only per-job fields the backend persists (mirror of MC_OWNED_FIELDS in
 * McOpsDashboardService). Everything else on a job is re-hydrated from Zoho on
 * read, so it must NOT be uploaded: sending the whole ~120kB snapshot on a
 * throttled connection never finishes before the user navigates away (and
 * overflows the 64kB fetch-keepalive cap on unload). Slimming the body to just
 * these makes the POST a few KB, so it completes fast and survives a refresh.
 */
const MC_OWNED_FIELDS = [
  "notes", "reminders", "introAttempts",
  "riskFlags", "riskNote", "riskTypeLD",
  "s2Notes", "s2YN",
  "paymentMethod", "paymentType", "cardLast4", "callBackRequested",
  "introCallDone", "inProgressCallDone", "inProgressCallDoneAt",
  "salesRepDraft", "opsDraft", "teamNoteDraft",
  "templatesSent", "pdfGenerated",
  "postMoveNote", "postMoveClosed",
  "storageNotes", "shuttleIncluded", "transitNote", "warehouseReceivedDate", "transitPaymentMethod",
  "deliveryDayCompleted", "etaConfirmed",
  "inProgressFeedback", "callbackDate", "callbackTime", "startTime", "unpackingDate",
  "contractSigned",
  "coiCompletedPickup", "coiCompletedDelivery",
  "carpenterIncluded", "assemblyIncluded", "disassemblyIncluded", "tvServiceIncluded", "disposalIncluded",
  "stairsAtPickup", "stairsFlights", "stairsAtDelivery", "stairsFlightsDelivery",
  "storageStatus", "automatedFlowStarted",
  "sentMessages",
];

/** True if a value is worth persisting (mirrors isMeaningful() on the backend). */
const isMeaningful = (v) => {
  if (typeof v === "boolean") return v === true;
  if (Array.isArray(v)) return v.length > 0;
  if (v && typeof v === "object") return Object.keys(v).length > 0;
  if (typeof v === "string") return v.trim() !== "";
  return v !== null && v !== undefined;
};

/**
 * Reduce the snapshot to only what the backend stores: the small top-level
 * lists, plus ONLY the jobs that actually carry a meaningful MC-owned override
 * (id + those fields). An admin coordinator sees hundreds of jobs; sending all
 * of them — even slimmed to MC fields — is still hundreds of KB, which never
 * finishes uploading on a throttled connection before a refresh and overflows
 * the 64kB keepalive cap. Since the backend rebuilds jobOverrides from scratch
 * on every save and re-hydrates all Zoho data on read, omitting override-less
 * jobs is lossless — and shrinks a typical edit to a single ~1kB job.
 */
const slimState = (state) => {
  if (!state) return state;
  const jobs = [];
  (state.jobs || []).forEach((j) => {
    const slim = { id: j.id };
    let hasOverride = false;
    MC_OWNED_FIELDS.forEach((k) => {
      if (j[k] !== undefined && isMeaningful(j[k])) {
        slim[k] = j[k];
        hasOverride = true;
      }
    });
    if (hasOverride) jobs.push(slim);
  });
  return {
    jobs,
    escalations: state.escalations || [],
    reminders: state.reminders || [],
    notifInternal: state.notifInternal || [],
    notifExternal: state.notifExternal || [],
    searchHistory: state.searchHistory || [],
  };
};

let pendingSnapshot = null; // latest state waiting to be persisted
let inFlight = false;

const postState = async (state, dispatch) => {
  try {
    await axios.post(`${getApiBasePath()}/state`, slimState(state), mcConfig());
    // savedAt bumps on every successful persist so the UI can flash a
    // "Saved" confirmation (the coordinator asked to SEE that it saved).
    dispatch(setMcOpsMeta({ error: null, savedAt: Date.now() }));
  } catch (error) {
    console.error("MC Ops: state not persisted —", error.message);
    dispatch(setMcOpsMeta({ error: error.message, saveFailedAt: Date.now() }));
  }
};

const flushPending = async (dispatch) => {
  if (inFlight || pendingSnapshot == null) return;
  const snapshot = pendingSnapshot;
  pendingSnapshot = null; // edits during the await re-arm it below
  inFlight = true;
  await postState(snapshot, dispatch);
  inFlight = false;
  if (pendingSnapshot != null) flushPending(dispatch); // last write wins
};

export const saveMcOpsState = (next) => (dispatch, getState) => {
  const state = next || getState().mcOpsDashboard?.data;
  dispatch(setMcOpsState(state)); // local update stays instant
  if (!WRITE_BACK_ENABLED || state == null) return;
  // Persist immediately. The payload is now tiny (only override-bearing jobs),
  // so there is no request storm to debounce away — and NOT waiting means a
  // quick refresh right after an edit can't drop the save. single-flight below
  // still coalesces a burst (e.g. tabbing through several fields) into one POST
  // at a time, newest snapshot winning.
  pendingSnapshot = state;
  flushPending(dispatch);
};

/**
 * Persist any pending edit immediately, surviving a page unload. Uses fetch
 * keepalive (which — unlike navigator.sendBeacon — can still carry the Bearer
 * header the API needs) so a refresh mid-edit does not drop the last change.
 * Wire this to a `pagehide`/`beforeunload` handler.
 */
export const flushMcOpsState = () => (dispatch, getState) => {
  const state = pendingSnapshot || getState().mcOpsDashboard?.data;
  pendingSnapshot = null;
  if (!WRITE_BACK_ENABLED || state == null) return;

  const cfg = mcConfig();
  let url = `${getApiBasePath()}/state`;
  if (cfg.params && cfg.params.view_as) {
    url += `?view_as=${encodeURIComponent(cfg.params.view_as)}`;
  }
  // Best-effort on unload. This is cross-origin (kpi → stage) with an auth
  // header, so the CORS preflight often can't complete during unload and the
  // fetch rejects — swallow it (both sync throw and async rejection) so it
  // never surfaces as an "Uncaught (in promise)". Normal edits are already
  // persisted immediately by saveMcOpsState, so this is only a backstop.
  try {
    const p = fetch(url, {
      method: "POST",
      headers: cfg.headers,
      body: JSON.stringify(slimState(state)),
      keepalive: true,
    });
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch (e) {
    /* best-effort on unload */
  }
};

/**
 * Live outbound side-effects. Fire-and-forget: the local state already reflects
 * the action, so a transport failure must never block the coordinator. Errors
 * are logged, not surfaced.
 */
export const placeCall = (phone) => async () => {
  try {
    await axios.post(`${getApiBasePath()}/call`, { phone }, mcConfig());
  } catch (error) {
    console.error("MC Ops: click-to-call failed —", error.message);
  }
};

export const postEscalation = (payload) => async () => {
  try {
    await axios.post(`${getApiBasePath()}/escalation`, payload, mcConfig());
  } catch (error) {
    console.error("MC Ops: escalation Cliq post failed —", error.message);
  }
};

export const sendClientSms = (payload) => async (dispatch) => {
  try {
    const { data } = await axios.post(`${getApiBasePath()}/sms`, payload, mcConfig());
    // The endpoint returns 200 with {success:false, error} when Talkdesk
    // rejects the send (bad/misconfigured number, token, etc.) — surface it
    // instead of silently showing "sent".
    if (data && data.success === false) {
      dispatch(setMcOpsMeta({ smsResult: { ok: false, error: data.error || "Text failed", at: Date.now() } }));
      return data;
    }
    dispatch(setMcOpsMeta({ smsResult: { ok: true, error: null, at: Date.now() } }));
    return data;
  } catch (error) {
    const msg = error?.response?.data?.error || error.message;
    console.error("MC Ops: SMS send failed —", msg);
    dispatch(setMcOpsMeta({ smsResult: { ok: false, error: msg, at: Date.now() } }));
    return { success: false, error: msg };
  }
};

export const generateConfirmationPdf = (job) => async () => {
  try {
    await axios.post(`${getApiBasePath()}/confirmation-pdf`, job, mcConfig());
  } catch (error) {
    console.error("MC Ops: confirmation PDF failed —", error.message);
  }
};

/** Write a job action back to Zoho (stage change for "confirmed", audit note otherwise). */
export const syncJobEvent = (payload) => async (dispatch) => {
  // No lead behind this job (e.g. an unlinked fixture): it can't reach Zoho.
  // Say so instead of silently keeping the note dashboard-only.
  if (!payload || !payload.leadId) {
    dispatch(setMcOpsMeta({
      noteResult: { ok: false, linked: false, error: "This job isn’t linked to Zoho — saved to the dashboard only.", at: Date.now(), event: payload?.event },
    }));
    return { success: false, error: "not linked" };
  }
  try {
    const { data } = await axios.post(`${getApiBasePath()}/job-event`, payload, mcConfig());
    if (data && data.success === false) {
      dispatch(setMcOpsMeta({ noteResult: { ok: false, linked: true, error: data.error || "Zoho sync failed", at: Date.now(), event: payload.event } }));
      return data;
    }
    dispatch(setMcOpsMeta({ noteResult: { ok: true, linked: true, error: null, at: Date.now(), event: payload.event } }));
    return data;
  } catch (error) {
    const msg = error?.response?.data?.error || error.message;
    console.error("MC Ops: Zoho job-event failed —", msg);
    dispatch(setMcOpsMeta({ noteResult: { ok: false, linked: true, error: msg, at: Date.now(), event: payload.event } }));
    return { success: false, error: msg };
  }
};

/**
 * Notes Hub → the lead's notes-table entry, in the background: no toast, no
 * meta — the per-note "synced" feedback already comes from syncJobEvent("note").
 * Failures are logged.
 */
export const syncNotesHub = (payload) => async () => {
  if (!payload || !payload.leadId) return;
  try {
    await axios.post(`${getApiBasePath()}/job-event`, payload, mcConfig());
  } catch (error) {
    console.error("MC Ops: notes hub sync failed —", error.message);
  }
};

export const postTeamNote = (payload) => async () => {
  try {
    await axios.post(`${getApiBasePath()}/team-note`, payload, mcConfig());
  } catch (error) {
    console.error("MC Ops: team note failed —", error.message);
  }
};

export const postDailySummary = (payload) => async () => {
  try {
    await axios.post(`${getApiBasePath()}/daily-summary`, payload, mcConfig());
  } catch (error) {
    console.error("MC Ops: daily summary failed —", error.message);
  }
};

export const sendClientEmail = (payload) => async (dispatch) => {
  try {
    const { data } = await axios.post(`${getApiBasePath()}/email`, payload, mcConfig());
    if (data && data.success === false) {
      dispatch(setMcOpsMeta({ emailResult: { ok: false, error: data.error || "Email failed", at: Date.now() } }));
      return data;
    }
    dispatch(setMcOpsMeta({ emailResult: { ok: true, error: null, at: Date.now() } }));
    return data;
  } catch (error) {
    const msg = error?.response?.data?.error || error.message;
    console.error("MC Ops: email send failed —", msg);
    dispatch(setMcOpsMeta({ emailResult: { ok: false, error: msg, at: Date.now() } }));
    return { success: false, error: msg };
  }
};

/** Generate a document (POA/BOL/Contract/Release/Wire), email it + attach to Zoho. */
export const sendDocument = (payload) => async () => {
  try {
    await axios.post(`${getApiBasePath()}/document`, payload, mcConfig());
  } catch (error) {
    console.error("MC Ops: document send failed —", error.message);
  }
};

// ---- shared SMS/email template library --------------------------------------
export const fetchTemplates = () => async (dispatch) => {
  try {
    const { data } = await axios.get(`${getApiBasePath()}/templates`, mcConfig());
    dispatch(setMcOpsMeta({ templates: data?.templates || [] }));
  } catch (error) {
    console.error("MC Ops: templates load failed —", error.message);
  }
};

export const createTemplate = (tpl) => async (dispatch) => {
  await axios.post(`${getApiBasePath()}/templates`, tpl, mcConfig());
  return dispatch(fetchTemplates());
};

export const updateTemplate = (id, tpl) => async (dispatch) => {
  await axios.put(`${getApiBasePath()}/templates/${id}`, tpl, mcConfig());
  return dispatch(fetchTemplates());
};

export const deleteTemplate = (id) => async (dispatch) => {
  await axios.delete(`${getApiBasePath()}/templates/${id}`, mcConfig());
  return dispatch(fetchTemplates());
};

// ---- admin: coordinator switch (act as) -------------------------------------
export const fetchCoordinators = () => async (dispatch) => {
  try {
    const { data } = await axios.get(`${getApiBasePath()}/coordinators`, mcConfig());
    dispatch(setMcOpsMeta({ coordinators: data?.coordinators || [] }));
  } catch (error) {
    // 403 for non-admins is expected — just leave the list empty.
    dispatch(setMcOpsMeta({ coordinators: [] }));
  }
};

/** Switch the admin's active coordinator (or "" for their own/all view). */
export const applyViewAs = (id) => async (dispatch) => {
  setViewAs(id || "");
  dispatch(setMcOpsMeta({ viewAs: id || "" }));
  await dispatch(getMcOpsState({ showLoading: true }));
  dispatch(fetchTemplates());
};

// ---- Roadway Buddy: time off and the buddy covering it ----

const setTimeOff = (data) =>
  setMcOpsMeta({
    timeOff: {
      loaded: true,
      me: data?.me || null,
      canApprove: data?.canApprove === true,
      team: data?.team || [],
      requests: data?.requests || [],
      error: null,
    },
  });

export const fetchTimeOff = () => async (dispatch, getState) => {
  try {
    const { data } = await axios.get(`${getApiBasePath()}/time-off`, mcConfig());
    dispatch(setTimeOff(data));
  } catch (error) {
    const prev = getState().mcOpsDashboard?.meta?.timeOff || {};
    dispatch(setMcOpsMeta({ timeOff: { ...prev, loaded: true, error: error?.response?.status || "failed" } }));
  }
};

/**
 * Request, decide or cancel; resolves with "" or the server's reason for
 * refusing, and refreshes the list either way it succeeds.
 */
const timeOffCall = (path, body) => async (dispatch) => {
  try {
    const { data } = await axios.post(`${getApiBasePath()}/time-off${path}`, body, mcConfig());
    dispatch(setTimeOff(data));
    return "";
  } catch (error) {
    return error?.response?.data?.message || "Not saved. Try again.";
  }
};

export const requestTimeOff = (body) => timeOffCall("", body);
export const decideTimeOff = (id, decision, note) => timeOffCall(`/${id}/decision`, { decision, note });
export const cancelTimeOff = (id) => timeOffCall(`/${id}/cancel`, {});

// ---- Auto-assignment admin API (docs/mc-ops/auto-assignment-design.md) ----
// Admin only: the backend accepts the token, never the mc_user_id parameter.

const adminConfig = (params = {}) => ({
  headers: apiHeaders(),
  ...(Object.keys(params).length ? { params } : {}),
});

/** Coordinators with their auto-assignment settings and today's load. */
export const fetchAutoAssignCoordinators = () => async (dispatch) => {
  try {
    const { data } = await axios.get(`${getApiBasePath()}/auto-assign/coordinators`, adminConfig());
    dispatch(
      setMcOpsMeta({
        autoAssign: {
          mode: data?.mode || "suggest",
          options: data?.options || {},
          coordinators: data?.coordinators || [],
          error: null,
        },
      }),
    );
  } catch (error) {
    dispatch(
      setMcOpsMeta({
        autoAssign: { mode: "suggest", options: {}, coordinators: [], error: error?.response?.status || "failed" },
      }),
    );
  }
};

/** Save one coordinator's settings; returns the saved settings or throws. */
export const saveCoordinatorSettings = (userId, settings) => async (dispatch, getState) => {
  const { data } = await axios.put(
    `${getApiBasePath()}/auto-assign/coordinators/${userId}`,
    settings,
    adminConfig(),
  );
  const current = getState().mcOpsDashboard?.meta?.autoAssign;
  if (current) {
    dispatch(
      setMcOpsMeta({
        autoAssign: {
          ...current,
          coordinators: current.coordinators.map((c) =>
            c.id === userId ? { ...c, configured: true, settings: data.settings } : c,
          ),
        },
      }),
    );
  }
  return data.settings;
};

/** The engine's decision log for the last `days` days. */
export const fetchAssignmentLog = (days = 7) => async (dispatch) => {
  try {
    const { data } = await axios.get(`${getApiBasePath()}/auto-assign/log`, adminConfig({ days }));
    dispatch(
      setMcOpsMeta({
        assignmentLog: { mode: data?.mode || "suggest", days, assignments: data?.assignments || [], error: null },
      }),
    );
  } catch (error) {
    dispatch(
      setMcOpsMeta({
        assignmentLog: { mode: "suggest", days, assignments: [], error: error?.response?.status || "failed" },
      }),
    );
  }
};

/** Apply one suggestion now (writes the coordinator to Zoho). */
export const applyAssignment = (id) => async (dispatch, getState) => {
  const { data } = await axios.post(`${getApiBasePath()}/auto-assign/log/${id}/apply`, {}, adminConfig());
  const current = getState().mcOpsDashboard?.meta?.assignmentLog;
  if (current) {
    dispatch(
      setMcOpsMeta({
        assignmentLog: {
          ...current,
          assignments: current.assignments.map((a) => (a.id === id ? data.assignment : a)),
        },
      }),
    );
  }
  return data.assignment;
};

const normalize = (payload) => ({
  jobs: payload.jobs || [],
  escalations: payload.escalations || [],
  reminders: payload.reminders || [],
  notifInternal: payload.notifInternal || [],
  notifExternal: payload.notifExternal || [],
  unanswered24h: payload.unanswered24h || { calls: [], texts: [] },
  unanswered48h: payload.unanswered48h || { calls: [], texts: [] },
  // The past week, for Needs Attention's "Over 24 h" / "Over 48 h" columns (Asana 1220099429071097).
  unansweredOpen: payload.unansweredOpen || null,
  searchHistory: payload.searchHistory || [],
  createdAt: payload.createdAt || null,
});
